import { AxiosError } from 'axios';
import type { ProblemDetails } from '../api/types';

/**
 * Normalized API error. The PlatformAPI returns RFC 7807 problem+json bodies
 * from its endpoint helpers (with a stable `code`), plain JSON from the JWT
 * challenge/forbidden/exception handlers, and empty bodies for 429/404/405.
 */
export class PlatformApiError extends Error {
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly traceId: string | undefined;
  readonly detail: string | undefined;
  readonly title: string | undefined;

  constructor(input: {
    message: string;
    status?: number;
    code?: string;
    traceId?: string;
    detail?: string;
    title?: string;
  }) {
    super(input.message);
    this.name = 'PlatformApiError';
    this.status = input.status;
    this.code = input.code;
    this.traceId = input.traceId;
    this.detail = input.detail;
    this.title = input.title;
  }

  isUnauthorized(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isRateLimited(): boolean {
    return this.status === 429;
  }

  /** Best single-sentence message for the current context. */
  static fromAxios(error: unknown, fallback: string): PlatformApiError {
    const axiosError = error as AxiosError<ProblemDetails | string | undefined> | undefined;
    if (!axiosError || !axiosError.isAxiosError) {
      return new PlatformApiError({ message: fallback });
    }

    const response = axiosError.response;
    if (!response) {
      const offline =
        axiosError.code === 'ECONNABORTED'
          ? 'The request timed out. Check that PlatformAPI is running.'
          : 'Cannot reach the Platform service. Check your connection and that PlatformAPI is running.';
      return new PlatformApiError({ message: offline, code: 'network_error' });
    }

    const data = response.data;
    const problem = typeof data === 'object' && data !== null ? (data as ProblemDetails) : undefined;
    const status = response.status;

    if (status === 429) {
      return new PlatformApiError({
        status,
        code: 'rate_limited',
        title: 'Too many requests',
        message: 'Too many requests. Please wait a minute before trying again.',
      });
    }

    const detail = problem?.detail;
    const title = problem?.title;
    const code = problem?.code;
    const message = detail ?? title ?? fallback;
    return new PlatformApiError({ status, code, detail, title, traceId: problem?.traceId, message });
  }
}
